import React from "react";
import getConfig from "next/config";
import { SquareButton, SquareButtonColor, CompanyLogoOutlined } from "@themuse/design-system";

import { ContentModuleBaseAttributes } from "utils/orm";
import { getSponsorPlacement } from "./SponsorModule";

import styles from "./sponsorModule.module.scss";
import articleContainerStyles from "./articleContainer.module.scss";

export interface SponsorInlineBlock extends ContentModuleBaseAttributes {
    active: boolean;
    companyName: string;
    hasSponsorShip: boolean;
    legacyId: string;
    listicleContentType: string;
    logoImage: string;
    product: string;
    shortName: string;
    sponsorName: string;
}

/**
 * Creates an inline CTA module. This is a content module and is injected in Post process.
 * @example
 * import { SponsorInlineModule } from "components/SponsorInlineModule";
 * <SponsorInlineModule {...MOCK_Sponsor} />
 */
export const SponsorInlineModule: React.FC<SponsorInlineBlock> = ({
    active,
    companyName,
    hasSponsorShip,
    id, legacyId,
    logoImage,
    shortName,
    product
}) => {
    const { publicRuntimeConfig: { pilboxHost, staticAssetHost } } = getConfig();
    const placements = getSponsorPlacement({ active, hasSponsorShip, product });
    const roundupReg = new RegExp("roundup", "i");
    const isRoundup = roundupReg.test(product);
    const encodedUrl = encodeURIComponent(`${staticAssetHost}/${logoImage}`);
    const flags = "filter=antialias&mode=clip&opt=1&pos=top-left&prog=1&q=keep";
    const logoUrl = `${pilboxHost}/image.png?${flags}&url=${encodedUrl}&h=49&w=108`;
    const hasLogo = isRoundup;
    const logoClass = hasLogo ? styles.sponsorInlineLogo : " ";

    const moduleTrackingProps = {
        "data-tracking-event-type": "impression",
        "data-tracking-props": JSON.stringify({
            sp_impression_component: "tile",
            sp_impression_tile_type: "article_sponsor",
            sp_impression_position: "in_article",
            sp_page_section: "advice",
            sp_page_type: "content",
            sp_tile_entity_id: legacyId ? legacyId.toString() : null,
            sp_content_is_tile: false
        })
    };

    const clickTrackingProps = {
        sp_click_component_tile_type: "article_sponsor",
        sp_click_position: "in_article",
        sp_search_id: null,
        sp_page_type: "content",
        sp_page_section: "advice",
        sp_page_company_id: legacyId ? legacyId.toString() : null,
        sp_page_tab: null,
        sp_is_external_page: false,
        sp_page_entity_id: id
    };
    const profileTrackingProps = {
        ...clickTrackingProps,
        sp_click_component: "insert",
        sp_click_button_text: null,
        sp_click_target_page_position: "profile",
        sp_click_button_type: null,
        sp_click_insert_type: "logo",
    };
    const searchTrackingProps = {
        ...clickTrackingProps,
        sp_click_component: "button",
        sp_click_button_text: "learn more",
        sp_click_target_page_position: "search",
        sp_click_button_type: "job_search",
        sp_click_insert_type: null
    };

    const companyLogoProps = {
        className: styles.logoPill,
        companyUrl: `/profiles/${shortName}`,
        altText: `${companyName} Logo`,
        logoUrl,
        "data-tracking-props": JSON.stringify(profileTrackingProps),
        "data-tracking-event-type": "click"
    };

    const companyLogoTrackingProps = {
        "data-tracking-props": JSON.stringify(profileTrackingProps),
        "data-tracking-event-type": "click"
    };

    const SquareLinkButtonProps = {
        className: styles.squareButton,
        "data-tracking-props": JSON.stringify(searchTrackingProps),
        "data-tracking-event-type": "click",
        color: "mint" as SquareButtonColor,
        href: `/search/company/${shortName}`,
        target: "_blank"
    };

    return placements.inline ? (
        <aside className={`${articleContainerStyles.contentModule} ${styles.contentModule} ${styles.inline} ${logoClass}`} {...moduleTrackingProps}>
            {hasLogo ? <CompanyLogoOutlined extraLinkProps={companyLogoTrackingProps} {...companyLogoProps} /> : null}
            <p className={styles.sponsorInlineEyebrow}>EXPLORE JOBS AT</p>
            <p className={styles.sponsorInlineTitle}>{companyName}</p>
            <SquareButton {...SquareLinkButtonProps}>LEARN MORE</SquareButton>
        </aside>
    ) : null;
};
